import React, { useMemo, useState } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import useDateTime from "@/hooks/useDateTime";
import { TimelineLegend } from "./TimelineLegend";
import type { TimelineBarProps, TimeSegment } from "./types";

// Format a clock time (e.g., "8:00 AM" / "20:00"), with a "+N" day suffix when
// the segment lands on a later calendar day than the timeline's start.
function formatClock(d: Date, use24h: boolean, dayOffset: number): string {
  const h = d.getHours();
  const m = d.getMinutes();
  let label: string;
  if (use24h) {
    label = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  } else {
    const h12 = h % 12 || 12;
    label = `${h12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
  }
  return dayOffset >= 1 ? `${label} +${dayOffset}` : label;
}

// Format hour for axis labels (e.g., "8AM", "12PM", "2AM+1" for next day)
function formatAxisLabel(hour: number, use24h: boolean, spansMidnight: boolean): string {
  const actualHour = hour % 24;
  const isNextDay = hour >= 24;

  let label: string;
  if (use24h) {
    label = `${actualHour.toString().padStart(2, "0")}:00`;
  } else {
    const h12 = actualHour % 12 || 12;
    const ampm = actualHour >= 12 ? "PM" : "AM";
    label = `${h12}${ampm}`;
  }

  if (isNextDay && spansMidnight) {
    label += "+1";
  }

  return label;
}

// Get the time range boundaries from start time and total duration
// Returns hours as continuous from midnight (can be > 24 for next day)
function getTimeRange(
  startTime: string,
  segments: TimeSegment[],
  toUserTimezone: (d: Date) => Date,
): { minHour: number; maxHour: number; spansMidnight: boolean } {
  if (segments.length === 0) {
    // Default range: 8 AM to 6 PM
    return { minHour: 8, maxHour: 18, spansMidnight: false };
  }

  const startDate = toUserTimezone(new Date(startTime));
  const startHour = startDate.getHours();
  const startMinutes = startDate.getMinutes();
  const totalDurationHours = segments.reduce((sum, seg) => sum + seg.duration, 0);

  // Calculate end hour as continuous from start (can exceed 24)
  const totalMinutesFromMidnight = startHour * 60 + startMinutes + totalDurationHours * 60;
  const endHour = Math.ceil(totalMinutesFromMidnight / 60);

  const spansMidnight = endHour > 24;

  // Pad the range for visual buffer
  const minHour = Math.max(0, startHour - 1);
  const maxHour = Math.max(endHour + 1, minHour + 3);

  return { minHour, maxHour, spansMidnight };
}

type SegmentBar = {
  id: string;
  type: TimeSegment["type"];
  left: number;
  width: number;
  startLabel: string;
  endLabel: string;
};

export function TimelineBar({ startTime, segments }: TimelineBarProps) {
  const { colors } = useTheme();
  const { settings, toUserTimezone } = useDateTime();
  const use24h = settings.timeFormat === "24";

  // Which segment (by id) is hovered — drives its own local tooltip. Self-
  // contained (no global ToolTipProvider), the same way the date/mod pickers
  // render a local ContextMenu, so it works wherever the editor mounts.
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const { axisLabels, segmentBars } = useMemo(() => {
    const { minHour, maxHour, spansMidnight } = getTimeRange(startTime, segments, toUserTimezone);
    const totalMinutes = (maxHour - minHour) * 60;

    // Generate axis labels (every 2 hours)
    const axisLabels: { label: string; position: number }[] = [];
    for (let hour = minHour; hour <= maxHour; hour += 2) {
      const position = ((hour - minHour) / (maxHour - minHour)) * 100;
      axisLabels.push({
        label: formatAxisLabel(hour, use24h, spansMidnight),
        position,
      });
    }

    // Calculate segment positions and widths from cumulative durations
    const startDate = toUserTimezone(new Date(startTime));
    const startMidnight = new Date(startDate);
    startMidnight.setHours(0, 0, 0, 0);
    const dayOffsetOf = (d: Date) => Math.floor((d.getTime() - startMidnight.getTime()) / 86_400_000);
    let cumulativeMinutes = 0;

    const segmentBars: SegmentBar[] = segments.map((segment) => {
      const segmentStartMinutes = cumulativeMinutes;
      const segmentDurationMinutes = segment.duration * 60;
      cumulativeMinutes += segmentDurationMinutes;

      // Convert to position relative to the axis range
      const startOffset = startDate.getHours() * 60 + startDate.getMinutes() - minHour * 60;
      const left = ((startOffset + segmentStartMinutes) / totalMinutes) * 100;
      const width = (segmentDurationMinutes / totalMinutes) * 100;

      // Actual clock start/end for this segment on the timeline.
      const segStart = new Date(startDate.getTime() + segmentStartMinutes * 60_000);
      const segEnd = new Date(startDate.getTime() + (segmentStartMinutes + segmentDurationMinutes) * 60_000);

      return {
        id: segment.id,
        type: segment.type,
        left: Math.max(0, left),
        width: Math.max(1, Math.min(100 - left, width)),
        startLabel: formatClock(segStart, use24h, dayOffsetOf(segStart)),
        endLabel: formatClock(segEnd, use24h, dayOffsetOf(segEnd)),
      };
    });

    return { axisLabels, segmentBars };
  }, [startTime, segments, use24h, toUserTimezone]);

  // Type colors: worked=green, meal=amber, break=gray, non-worked=primary
  const workedColor = colors.complete?.border || "#10b981";
  const mealColor = colors.pending?.border || "#f59e0b";
  const breakColor = colors.neutral?.border || "#6b7280";
  const nonWorkedColor = colors.primary || "#3b82f6";

  // Get color for segment type
  const getSegmentColor = (type: TimeSegment["type"]) => {
    switch (type) {
      case "worked":
        return workedColor;
      case "meal":
        return mealColor;
      case "break":
        return breakColor;
      case "non-worked":
        return nonWorkedColor;
    }
  };

  const hoveredBar = segmentBars.find((bar) => bar.id === hoveredId) ?? null;

  return (
    <View style={styles.container}>
      {/* Axis labels */}
      <View style={styles.axisContainer}>
        {axisLabels.map(({ label, position }) => (
          <ThemedText key={label} style={[styles.axisLabel, { left: `${position}%`, color: colors.textSecondary }]}>
            {label}
          </ThemedText>
        ))}
      </View>

      {/* Timeline bar track (wrapped so a segment tooltip escapes the track's clip) */}
      <View style={styles.trackWrap}>
        <View style={[styles.track, { backgroundColor: colors.backgroundSecondary, borderColor: colors.border }]}>
          {segmentBars.map((bar) => (
            <Pressable
              key={bar.id}
              onHoverIn={() => setHoveredId(bar.id)}
              onHoverOut={() => setHoveredId((prev) => (prev === bar.id ? null : prev))}
              style={[
                styles.segment,
                {
                  left: `${bar.left}%`,
                  width: `${bar.width}%`,
                  backgroundColor: getSegmentColor(bar.type),
                },
                hoveredId === bar.id && styles.segmentHovered,
              ]}
            />
          ))}
        </View>

        {/* Per-segment tooltip, anchored above the hovered segment's center */}
        {hoveredBar && (
          <View
            pointerEvents="none"
            style={[
              styles.tooltip,
              {
                left: `${hoveredBar.left + hoveredBar.width / 2}%`,
                backgroundColor: colors.background,
                borderColor: colors.border,
              },
            ]}
          >
            {/* Start → End clock times */}
            <ThemedText numberOfLines={1} style={[styles.rangeTime, { color: colors.text }]}>
              {hoveredBar.startLabel}
            </ThemedText>
            <ThemedText style={[styles.rangeArrow, { color: colors.placeholder }]}>→</ThemedText>
            <ThemedText numberOfLines={1} style={[styles.rangeTime, { color: colors.text }]}>
              {hoveredBar.endLabel}
            </ThemedText>
          </View>
        )}
      </View>

      {/* Legend */}
      <TimelineLegend />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 4,
  },
  axisContainer: {
    position: "relative",
    height: 16,
    marginBottom: 2,
  },
  axisLabel: {
    position: "absolute",
    fontSize: 10,
    transform: [{ translateX: -12 }],
  },
  track: {
    position: "relative",
    height: 24,
    borderRadius: 6,
    borderWidth: StyleSheet.hairlineWidth,
    overflow: "hidden",
  },
  segment: {
    position: "absolute",
    top: 0,
    bottom: 0,
    borderRadius: 4,
  },
  segmentHovered: {
    opacity: 0.8,
  },
  trackWrap: {
    position: "relative",
  },
  tooltip: {
    position: "absolute",
    bottom: "100%",
    marginBottom: 6,
    transform: [{ translateX: -70 }],
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    gap: 6,
    minWidth: 140,
    zIndex: 2,
  },
  rangeTime: {
    fontSize: 12,
    fontWeight: "600",
  },
  rangeArrow: {
    fontSize: 12,
  },
});
